import React, { useState, useEffect, useCallback } from 'react';
import {
  TruckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { fetchShipments } from '../api/marketplaceApi';

const SupplierFulfilment: React.FC = () => {
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filter === 'all' ? {} : { status: filter };
      const response = await fetchShipments(params);
      setShipments(response.data?.items || response.data || response.items || response || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load shipments');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusConfig: Record<string, { bg: string; text: string; icon: React.ElementType; color: string }> = {
    preparing: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: ClockIcon, color: 'from-amber-400 to-orange-500' },
    ready_for_dispatch: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: TruckIcon, color: 'from-blue-400 to-indigo-500' },
    in_transit: { bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-700', icon: TruckIcon, color: 'from-indigo-400 to-purple-500' },
    delivered: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: CheckCircleIcon, color: 'from-emerald-400 to-teal-500' },
    exception: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: ExclamationTriangleIcon, color: 'from-red-400 to-rose-500' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <TruckIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Fulfilment</h1>
              <p className="text-orange-100 mt-1">Manage shipments and track deliveries</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
              <ArrowPathIcon className="w-5 h-5" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'preparing', 'ready_for_dispatch', 'in_transit', 'delivered', 'exception'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all ${filter === f ? 'bg-orange-600 text-white shadow-lg shadow-orange-200' : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300'}`}>
            {f === 'all' ? 'All' : f.replace('_', ' ')}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-orange-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-orange-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error}</p>
          <button onClick={fetchData} className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Retry</button>
        </div>
      ) : shipments.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-red-100 rounded-2xl flex items-center justify-center mx-auto">
            <TruckIcon className="w-10 h-10 text-orange-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mt-4">No shipments yet</h3>
          <p className="text-gray-500 mt-2">Create shipments from your confirmed orders</p>
        </div>
      ) : (
        <div className="space-y-4">
          {shipments.map((s: any) => {
            const st = statusConfig[s.status] || statusConfig.preparing;
            const StatusIcon = st.icon;
            return (
              <div key={s.id} className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all overflow-hidden">
                <div className="flex">
                  {/* Status indicator */}
                  <div className={`w-2 bg-gradient-to-b ${st.color}`}></div>

                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${st.color} flex items-center justify-center shadow-md`}>
                          <StatusIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-orange-500 uppercase tracking-wider">
                            {s.shipment_number || `SHP-${s.id}`}
                          </p>
                          <h3 className="text-lg font-bold text-gray-900 mt-0.5">
                            Order: {s.supplier_order_number || s.order_number || 'N/A'}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {s.carrier || 'Standard Delivery'} • {s.tracking_number || 'No tracking'}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${st.bg} ${st.text}`}>
                        <StatusIcon className="w-4 h-4" />
                        {(s.status || 'preparing').replace('_', ' ')}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-4 gap-3">
                      <div className="p-3 bg-gray-50 rounded-xl">
                        <p className="text-xs font-bold text-gray-400">Origin</p>
                        <p className="text-sm font-semibold text-gray-700 mt-1 flex items-center gap-1">
                          <MapPinIcon className="w-3.5 h-3.5" />
                          {s.origin || s.fulfilment_location || 'N/A'}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-xl">
                        <p className="text-xs font-bold text-gray-400">Destination</p>
                        <p className="text-sm font-semibold text-gray-700 mt-1 flex items-center gap-1">
                          <MapPinIcon className="w-3.5 h-3.5" />
                          {s.destination || s.delivery_address || 'N/A'}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-xl">
                        <p className="text-xs font-bold text-gray-400">Shipped</p>
                        <p className="text-sm font-semibold text-gray-700 mt-1">
                          {s.shipped_at ? new Date(s.shipped_at).toLocaleDateString() : 'Not yet'}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-xl">
                        <p className="text-xs font-bold text-gray-400">Estimated</p>
                        <p className="text-sm font-semibold text-gray-700 mt-1">
                          {s.estimated_delivery ? new Date(s.estimated_delivery).toLocaleDateString() : 'TBD'}
                        </p>
                      </div>
                    </div>

                    {/* Evidence */}
                    {s.evidence && s.evidence.length > 0 && (
                      <div className="mt-4 flex items-center gap-2">
                        <PhotoIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-xs text-gray-500">{s.evidence.length} evidence files attached</span>
                      </div>
                    )}

                    {/* Exception notes */}
                    {s.status === 'exception' && s.exception_notes && (
                      <div className="mt-4 p-3 bg-red-50 rounded-xl border border-red-200">
                        <p className="text-xs font-bold text-red-600 uppercase mb-1">Exception</p>
                        <p className="text-sm text-red-700">{s.exception_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SupplierFulfilment;
